import { getSeoConfig } from "./config";
import { buildJsonLd } from "./schema";
import { isEntityIndexable, getRobotsContent } from "./quality";
import { parseRoute, isIndexableRoute, isNoindexOnlyPath } from "./routes";
import { buildCanonicalUrl, absoluteUrl } from "./url";

function fallbackImage(config) {
  const image = String(config.defaultImage || "").trim();
  if (!image) return "";
  return image.startsWith("http://") || image.startsWith("https://")
    ? image
    : absoluteUrl(image, config);
}

function routeTitle(routeKey, entity, config) {
  switch (routeKey) {
    case "home":
      return config.defaultTitle;
    case "watch":
      return `Watch Local Stories | ${config.brandName}`;
    case "series":
      return `${entity?.title || "Series"} | ${config.brandName}`;
    case "video":
      return `${entity?.title || "Video"} | ${config.brandName}`;
    case "profile":
      return `${entity?.displayName || entity?.userName || "Creator"} | ${config.brandName}`;
    case "business":
      return `${entity?.displayName || entity?.companyName || "Business"} | ${config.brandName}`;
    case "capsule":
      return `${entity?.title || entity?.headline || "Time Capsule"} | ${config.brandName}`;
    case "raffle":
      return `${entity?.title || "Raffle"} | ${config.brandName}`;
    default:
      return config.defaultTitle;
  }
}

function routeDescription(routeKey, entity, config) {
  const entityDescription = String(entity?.description || entity?.message || "").trim();
  if (entityDescription) return entityDescription.slice(0, 160);

  switch (routeKey) {
    case "watch":
      return "Stream movies and series curated by Inssa.";
    case "profile":
      return "Discover public creator profiles and their latest activity on Inssa.";
    case "business":
      return "Explore local businesses and sponsored capsules in your area.";
    case "capsule":
      return "Discover public time capsules around you on Inssa.";
    case "raffle":
      return "Join the latest Inssa raffle experiences and events.";
    default:
      return config.defaultDescription;
  }
}

function isProdSeoEnvironment(config, env = {}) {
  const explicitEnv = String(env.REACT_APP_ENV || process.env.REACT_APP_ENV || "").trim().toLowerCase();
  if (explicitEnv && explicitEnv !== "prod" && explicitEnv !== "production") {
    return false;
  }

  try {
    const host = new URL(config.baseUrl).hostname.toLowerCase();
    return host === "inssa.us" || host === "www.inssa.us";
  } catch {
    return false;
  }
}

export function getSeoForRoute(pathname, entityData = {}, env = {}) {
  const config = getSeoConfig(env);
  const parsed = parseRoute(pathname);
  const routeKey = parsed.routeKey;
  const eligibleRoute = isIndexableRoute(routeKey);
  const forcedNoindex = isNoindexOnlyPath(pathname) || !isProdSeoEnvironment(config, env);
  const entity = entityData || {};
  const entityIndexable = isEntityIndexable(routeKey, entity);
  const robots = getRobotsContent({
    routeKey,
    eligibleRoute,
    entityIndexable,
    forceNoindex: forcedNoindex,
  });
  const title = routeTitle(routeKey, entity, config);
  const description = routeDescription(routeKey, entity, config);
  const canonical = eligibleRoute
    ? buildCanonicalUrl(routeKey, entity, config)
    : absoluteUrl(parsed.path, config);
  const image =
    String(entity?.image || entity?.posterUrl || entity?.avatarUrl || "").trim() ||
    fallbackImage(config);
  const jsonLd = buildJsonLd(routeKey, entity, { canonical, title, description }, config);
  const indexable = robots.startsWith("index");

  return {
    routeKey,
    path: parsed.path,
    title,
    description,
    canonical,
    robots,
    indexable,
    og: {
      title,
      description,
      type: routeKey === "video" ? "video.other" : "website",
      url: canonical,
      image,
      siteName: config.siteName,
      locale: config.locale,
    },
    twitter: {
      card: config.twitterCard,
      site: config.twitterSite,
      title,
      description,
      image,
    },
    jsonLd,
  };
}

export default getSeoForRoute;
